<template>
    <div class="flex flex-column relative">
        <listing-batch
            v-show="selection.length > 0"
            :listinghash="listinghash"
            :selection="selection"
            :enable-all-pages="display.enableAllPages"
        />
        <el-table
            ref="table"
            height="100%"
            style="width: 100%"
            class="fixed-row-height"
            :class="listingClasses"
            :data="rows"
            :default-sort="defaultSort"
            border
            @select="selectOneRow"
            @select-all="selectAllRow"
            @sort-change="sendOrder"
            @header-dragend="updateColumnWidth"
        >
            <!-- BATCH -->
            <el-table-column
                v-if="hasBatchAction"
                fixed
                type="selection"
                prop="Batch"
                label=""
                width="41"
            >
            </el-table-column>

            <!-- NAME -->
            <el-table-column
                v-if="mainColumn"
                :key="mainColumn.name"
                fixed
                :sortable="mainColumn.sortable === true ? `custom` : false"
                :sort-method="sortColumn"
                :prop="mainColumn.name"
                :label="mainColumn.label"
                show-overflow-tooltip
                :min-width="convertedWidth(mainColumn.width, mainColumn.name)"
            >
                <template slot-scope="scope">
                    <router-link
                        v-if="
                            scope.row['formatted_' + mainColumn.name] &&
                            typeof scope.row['formatted_' + mainColumn.name] !== 'string'
                        "
                        class="subcell semibold gray-100"
                        :to="
                            scope.row['formatted_' + mainColumn.name] !== undefined
                                ? scope.row['formatted_' + mainColumn.name].link
                                : scope.row[mainColumn.name].link
                        "
                        >{{
                            scope.row['formatted_' + mainColumn.name] !== undefined
                                ? scope.row['formatted_' + mainColumn.name].input
                                : scope.row[mainColumn.name].input
                        }}</router-link
                    >
                    <div
                        v-else
                        class="subcell semibold"
                        v-html="
                            scope.row['formatted_' + mainColumn.name] !== undefined
                                ? scope.row['formatted_' + mainColumn.name]
                                : scope.row[mainColumn.name]
                        "
                    ></div>
                </template>
            </el-table-column>

            <!-- ALL COLUMNS -->
            <el-table-column
                v-for="header in displayedColumns"
                :key="`header-${header.name}-${header.rank}`"
                :prop="header.name"
                :label="header.label"
                :render-header="labelTooltiped"
                :sort-by="header.name"
                show-overflow-tooltip
                :sortable="header.sortable === true ? `custom` : false"
                :sort-method="sortColumn"
                header-align="left"
                :align="header.alignment"
                resizable
                :min-width="convertedWidth(header.width, header.name)"
            >
                <template slot-scope="scope">
                    <template
                        v-if="
                            scope.row['formatted_' + header.name] !== undefined &&
                            scope.row['formatted_' + header.name] !== null
                        "
                    >
                        <template
                            v-if="
                                typeof scope.row['formatted_' + header.name] === 'object' &&
                                scope.row['formatted_' + header.name].type === 'component'
                            "
                        >
                            <component
                                :is="scope.row['formatted_' + header.name].name"
                                :key="`${header.name}_${scope.row.id}`"
                                v-bind="scope.row['formatted_' + header.name].params"
                                :listinghash="listinghash"
                            ></component>
                        </template>
                        <div
                            v-else
                            class="subcell"
                            v-html="scope.row['formatted_' + header.name]"
                        />
                    </template>
                    <template v-else>{{ scope.row[header.name] }}</template>
                </template>
            </el-table-column>

            <!-- CONTEXT MENU -->
            <el-table-column
                v-if="hasContextMenu"
                fixed="right"
                label=""
                width="40"
                class-name="el-table-column--contextmenu"
            >
                <template slot-scope="scope">
                    <listing-table-element-menu
                        :id="scope.row.id"
                        :items="scope.row.contextmenu.items"
                    />
                </template>
            </el-table-column>
        </el-table>
    </div>
</template>

<script>
import _ from 'lodash';
import ListingBatch from './ListingBatch.vue';
import ListSelector from './ListSelector.vue';
import TagStatus from 'vcomponents/common/TagStatus/TagStatus.vue';
import CellCheckbox from './CellCheckbox.vue';
import PersonList from 'vcomponents/utilities/PersonList.vue';
import ChipLabel from 'vcomponents/utilities/ChipLabel.vue';
import ListingDropdown from 'vcomponents/listing/ListingDropdown.vue';
import EventValue from 'vcomponents/utilities/EventValue.vue';
import ObjectValue from 'vcomponents/utilities/ObjectValue.vue';
import DateValue from 'vcomponents/utilities/DateValue.vue';
import DateNoTimeValue from 'vcomponents/utilities/DateNoTimeValue.vue';
import ObjectLinkValue from 'vcomponents/utilities/ObjectLinkValue.vue';
import OcrItemListAction from 'vcomponents/ocr/OcrItemListAction.vue';
import OcrItemState from 'vcomponents/ocr/OcrItemState.vue';
import ContextMenuMixins from 'mixins/ContextMenuMixins';
import TagRelationTypeColumn from 'vcomponents/listing/tableColumns/TagRelationTypeColumn.vue';
import ListingTableElementMenu from 'vcomponents/listing/ListingTableElementMenu.vue';

import { createNamespacedHelpers } from 'vuex';
const popupPanelMapActions = createNamespacedHelpers('popupPanel').mapActions;

export default {
    components: {
        'person-list': PersonList,
        'date-value': DateValue,
        'date-no-time-value': DateNoTimeValue,
        'event-value': EventValue,
        'object-value': ObjectValue,
        'object-link-value': ObjectLinkValue,
        'ocr-item-list-action': OcrItemListAction,
        'ocr-item-state': OcrItemState,
        ListingBatch,
        ChipLabel,
        ListingDropdown,
        ListSelector,
        CellCheckbox,
        TagRelationTypeColumn,
        ListingTableElementMenu,
        TagStatus,
        ProgressInvoiceProgressBar: () =>
            import('vcomponents/listing/ProgressInvoiceProgressBar/ProgressInvoiceProgressBar.vue'),
    },
    mixins: [ContextMenuMixins],
    props: {
        listinghash: {
            type: String,
            required: true,
        },
        rows: {
            type: Array,
            default() {
                return [];
            },
        },
        columns: {
            type: Array,
            default() {
                return [];
            },
        },
    },

    data() {
        return {
            ordering: false,
            selection: [],
            selectAll: false,
            width: {
                XS: 48,
                S: 96,
                M: 144,
                L: 176,
                XL: 196,
                XXL: 240,
            },
        };
    },

    computed: {
        listingClasses() {
            let classes = this.tableDensity === 'condensed' ? 'table-condensed' : '';

            if (!/^((?!chrome|android).)*safari/i.test(navigator.userAgent))
                classes += ' table-ellipsis';

            return classes;
        },

        hasContextMenu() {
            return (
                this.rows.filter((row) => {
                    return row.contextmenu !== undefined;
                }).length > 0
            );
        },

        displayedColumns() {
            // When displaying columns, we make sure this.selection is according with table selection
            if (this.$refs.table && this.selection.length !== this.$refs.table.selection.length) {
                this.selection = this.$refs.table.selection;
            }
            return this.columns.filter((col) => !col.isMain);
        },

        mainColumn() {
            const columnIndex = _.findIndex(this.columns, { isMain: true });

            if (columnIndex !== -1) {
                return this.columns[columnIndex];
            }

            return false;
        },

        display() {
            return this.$store.state[this.listinghash].display;
        },

        sorting() {
            return this.$store.state[this.listinghash].sorting;
        },

        hasBatchAction() {
            return this.$store.state[this.listinghash].batchActions.length > 0;
        },

        tableDensity() {
            return this.$store.state[this.listinghash].display.density;
        },

        listingname() {
            return this.$store.state[this.listinghash].listingname;
        },

        columnsSize() {
            return this.$store.state[this.listinghash].columnsSize[this.listingname];
        },

        defaultSort() {
            const sorting = this.$store.state[this.listinghash].sorting;
            if (sorting.field) {
                return {
                    prop: sorting.field,
                    order: sorting.direction === 'asc' ? 'ascending' : 'descending',
                };
            }
            return { prop: '' };
        },
    },

    created() {
        window.EventBus.$on('filters:apply', () => this.resetSelection());

        EventBus.$on('batchaction:callback', () => {
            this.resetSelection();
            this.$store.commit(`${this.listinghash}/hideDisplay`, 'batchActions');
            this.$store.dispatch(`${this.listinghash}/fetch`);
        });

        EventBus.$on('listing:updatePage', (payload) => {
            if (this.listinghash === payload.listing) {
                this.resetSelection();
            }
        });

        window.EventBus.$on('listing:sortUpdated', (payload) => {
            this.updateSortCaret(payload.field, payload.direction + 'ending');
        });
    },

    methods: {
        ...popupPanelMapActions(['open']),
        resetSelection() {
            this.$refs.table.clearSelection();
            this.selection = [];
        },

        labelTooltiped(h, { column }) {
            return h('el-tooltip', { props: { content: column.label, placement: 'top' } }, [
                h('span', {}, column.label),
            ]);
        },

        convertedWidth(value, name) {
            const column = this.columnsSize
                ? this.columnsSize.find((col) => col.name === name)
                : null;
            if (column) {
                return column.width.toString();
            }

            if (Object.keys(this.width).find((key) => key === value)) {
                return this.width[value].toString();
            }
            return value.toString();
        },

        sortBy(column) {
            const newSorting = {
                field: column,
                direction: 'asc',
            };

            if (this.sorting.field === column && this.sorting.direction === 'asc') {
                newSorting.direction = 'desc';
            }

            this.$store.commit(`${this.listinghash}/updateSorting`, newSorting);
            this.$store.dispatch(`${this.listinghash}/fetch`);
        },

        selectOneRow(selection) {
            this.selection = selection;
            this.selectAll = this.selection.length === this.rows.length ? true : false;
            this.$store.state[this.listinghash].display.visibleAllPages = this.selectAll;
            if (!this.selectAll) {
                this.$store.state[this.listinghash].display.checkAllPages = false;
            }
        },

        selectAllRow(selection) {
            this.selection = selection;
            this.selectAll = this.selection.length === this.rows.length ? true : false;
            this.$store.state[this.listinghash].display.visibleAllPages = this.selectAll;
        },

        sendOrder: _.debounce(function (column) {
            if (this.ordering) {
                this.ordering = false;
                return;
            }
            const newOrder = column.order === 'descending' ? 'desc' : 'asc';
            const newSorting = {
                field: column.prop,
                direction: newOrder,
            };

            this.$store.commit(`${this.listinghash}/updateSorting`, newSorting);
            this.$store.dispatch(`${this.listinghash}/fetch`);
        }, 300),

        updateSortCaret(column, direction) {
            if (this.$refs.table === undefined) {
                return false;
            }
            this.$refs.table.clearSort();
            this.ordering = true;
            this.$refs.table.sort(column, direction);
        },

        updateColumnWidth(newWidth, oldWidth, column, event) {
            this.$store.dispatch(`${this.listinghash}/updateColumnsSettings`, {
                newWidth,
                oldWidth,
                column,
                event,
            });
        },

        sortColumn() {
            return 0;
        },
    },
};
</script>
